/*
__Seed builder__
  AUTO_GENERATED (Read only)
  API lib
*/

import * as Sentry from "@sentry/react";
import React, { useState, useEffect } from "react";
import useFetch from "react-fetch-hook";
import { API_URL } from "settings";

/** @module api **/

/**
 * Returns a hook to execute a GET request
 * @param {string} endpoint Relative path to SERVER_URL/api
 * @param {Object} queryArgs Query args of the request
 * @param {Object} options Request options (onCompleted, onError, includeAuth=true, captureErrors=true)
 * @returns GET hook
 * @example
 * const reqPlayers = useGet("/players", { name: "messi" })
 * // It is equal to /players?name=messi
 */
const useGet = (endpoint, queryArgs = {}, options = {}) =>
  usePoll(endpoint, queryArgs, options);

/**
 * Returns a hook to execute a GET request callable (like put, post, etc)
 * @param {string} endpoint Relative path to SERVER_URL/api
 * @param {Object} queryArgs Query args of the request
 * @param {Object} options Request options (onCompleted, onError, includeAuth=true, captureErrors=true)
 * @returns GET hook
 * @example
 * const [callGet, reqPlayers] = useGet("/players", { name: "messi" })
 * // It is equal to /players?name=messi
 */
const useGetCall = (endpoint, queryArgs = {}, options = {}) =>
  useMutate("GET", `${endpoint}/?${query(queryArgs)}`, options);

/**
* Returns a hook to execute a POST request
* @param {string} endpoint Relative path to SERVER_URL/api
* @param {Object} options Request options (onCompleted, onError, includeAuth=true, captureErrors=true)
* @returns POST hook
* @example
* const [callSave, reqSave] = usePost("/players", {
*   onCompleted: (data) => alert("Player saved")
* })
* ...
* // Execute request
* callSave({ name: "messi" })
*/
const usePost = (endpoint, options = {}) =>
  useMutate("POST", endpoint, options);

/**
* Returns a hook to execute a PUT request
* @param {string} endpoint Relative path to SERVER_URL/api
* @param {Object} options Request options (onCompleted, onError, includeAuth=true, captureErrors=true)
* @returns PUT hook
* @example
* const [callPut, reqPut] = usePut("/players", {
*   onCompleted: (data) => alert("Player modified")
* })
* ...
* // Execute request
* callPut({ id: 1, name: "messi" })
*/
const usePut = (endpoint, options = {}) =>
  useMutate("PUT", endpoint, options);

/**
* Returns a hook to execute a DELETE request
* @param {string} endpoint Relative path to SERVER_URL/api
* @param {Object} options Request options (onCompleted, onError, includeAuth=true, captureErrors=true)
* @returns DELETE hook
* @example
* const [callDelete, reqDelete] = useDelete("/players", {
*   onCompleted: (data) => alert("Player deleted")
* })
* ...
* // Execute request
* callDelete({ id: 1 })
*/
const useDelete = (endpoint, options = {}) =>
  useMutate("DELETE", endpoint, options);

const query = (params) => {
  let query = "";
  for (let param in params)
    if (params[param] != null)
      query += `${param}=${encodeURIComponent(params[param])}&`;
  return query;
};

const options = (method = "GET", body = {}) => {
  let res = {
    method: method,
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Authorization": `Token ${sessionStorage.getItem("token")}`
    }
  };
  if (method !== "GET")
    res["body"] = JSON.stringify(body);
  return res;
};

const usePoll = (endpoint, params, pollOptions = {}) => {
  const [status, setStatus] = useState({ data: null, isLoading: true, fetchStatus: true });
  useEffect(() => () => { setStatus({ data: null, isLoading: true, fetchStatus: true }) }, []);
  const optionsData = options("GET")
  if (pollOptions.includeAuth === false)
    delete optionsData.headers["Authorization"]
  const fetch = useFetch(`${API_URL}${endpoint}/?${query(params)}`, {
    ...optionsData,
    formatter: (response) => {
      if (!response.ok) throw response;

      return new Promise((resolve, reject) => {
        (async () => {
          try {
            let text = await response.text()
            setStatus({ ...status, isLoading: true, fetchStatus: false })
            try {
              return resolve(JSON.parse(text));
            } catch (e) { }
            return resolve(text)
          } catch (err) {
            return reject(err);
          }
        })()
      })
    },
    depends: [status.fetchStatus == true]
  });

  if ((fetch.data != null || fetch.error != null) && status.isLoading) {
    let data = null;
    if (fetch.error == null) {
      data = fetch.data;
      try {
        data = JSON.parse(fetch.data);
      } catch (e) { }
      if (pollOptions.onCompleted != null)
        pollOptions.onCompleted(data);
    } else {
      if (pollOptions.captureErrors !== false)
        Sentry.captureException(new Error(`${fetch.error.status} - ${fetch.error.statusText}: ${fetch.error.url}`));
      if (pollOptions.onError != null)
        pollOptions.onError(fetch.error);
    }
    setStatus({ data: data, isLoading: false });
  }
  const refetch = () => setStatus({ ...status, fetchStatus: true })

  return { ...fetch, loading: status.isLoading, loaded: status.data != null, data: status.data, refetch: refetch };
};

const useMutate = (method, endpoint, mutOptions = {}) => {
  const [call, setCall] = useState({ body: null, called: false });
  useEffect(() => () => setCall({ body: null, called: false }), []);
  const calling = (body) =>
    body ? setCall({ body: body }) : setCall({ body: {} });
  const optionsData = options(method, call.body)
  if (mutOptions.includeAuth === false)
    delete optionsData.headers["Authorization"]
  const fetch = useFetch(`${API_URL}${endpoint}${call.body && call.body.id ? "/" + call.body.id : ""}/`, {
    ...optionsData,
    formatter: (response) => {
      if (!response.ok) throw response;
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            let text = await response.text()
            try {
              return resolve(JSON.parse(text));
            } catch (e) { }
            return resolve(text)
          } catch (err) {
            return reject(err);
          }
        })()
      })
    },
    depends: [call.body != null]
  });

  if (fetch.error && fetch.error.status == null)
    fetch.error = new Response(null, { status: 503 })

  if (call.body != null && !call.called && fetch.isLoading)
    setCall({ ...call, called: true });
  if (call.body != null && call.called && !fetch.isLoading) {
    if (mutOptions.onCompleted != null && fetch.error == null) {
      let json = fetch.data;
      try {
        json = JSON.parse(fetch.data);
      } catch (e) { }
      mutOptions.onCompleted(json);
    }
    if (fetch.error != null) {
      if (mutOptions.captureErrors !== false)
        Sentry.captureException(new Error(`${fetch.error.status} - ${fetch.error.statusText}: ${fetch.error.url}`));
      if (mutOptions.onError != null)
        mutOptions.onError(fetch.error);
    }

    setCall({ body: null, called: false });
  }
  return [calling, { ...fetch, loading: fetch.isLoading, loaded: call.called, called: call.called }];
};

export { useGet, useGetCall, usePost, usePut, useDelete };